/**
 * 模拟AG-UI客户端
 * 用于与后端SSE流式接口通信
 */

import { API_BASE_URL2 , API_ENDPOINTS} from '../config';
import { formatReferencesToMarkdown } from './referenceFormatter';

// 模拟AG-UI客户端类
class AGUIClientMock {
  constructor(config) {
    this.config = config;
    this.transport = config.transport || 'sse';
    this.endpoint = config.endpoint;
    this.headers = config.headers || {};
    this.eventCallbacks = {};
    this.controller = null;
    this.currentMessageId = null; // 用于跟踪当前消息的ID
    this.pendingSearchResults = null; // 临时存储search_result类型的参考资料
    this.pendingKbSource = null; // 临时存储kb_source类型的知识库引用
    this.heartbeatTimer = null; // 心跳定时器
    // 可配置的超时时间（针对1小时长任务）
    this.connectionTimeout = config.connectionTimeout || 60 * 60 * 1000;
  }

  // 设置超时时间
  setConnectionTimeout(timeout) {
    this.connectionTimeout = timeout;
    console.log(`设置连接超时时间为: ${timeout}ms (${timeout/60000}分钟)`);
  }
  
  // 获取当前超时设置
  getConnectionTimeout() {
    return this.connectionTimeout;
  }

  // 设置headers
  setHeaders(headers) {
    this.headers = { ...this.headers, ...headers };
  }

  // 监听事件
  on(eventName, callback) {
    if (!this.eventCallbacks[eventName]) {
      this.eventCallbacks[eventName] = [];
    }
    this.eventCallbacks[eventName].push(callback);
    return this;
  }
  
  // 清除特定事件的所有监听器
  off(eventName) {
    if (this.eventCallbacks[eventName]) {
      this.eventCallbacks[eventName] = [];
    }
    return this;
  }
  
  // 清除所有事件监听器
  clearAllListeners() {
    this.eventCallbacks = {};
    return this;
  }

  // 触发事件
  _triggerEvent(eventName, data) {
    console.log(`触发事件: ${eventName}`, data);
    console.log('已注册的事件:', Object.keys(this.eventCallbacks)); // 打印所有已注册的事件名
    if (this.eventCallbacks[eventName]) {
      console.log(`${eventName}事件有${this.eventCallbacks[eventName].length}个监听器`);
      this.eventCallbacks[eventName].forEach(callback => callback(data));
    }
  }

  // 发送输入
  async sendInput(data) {

    
    if (this.transport === 'sse') {
      // 发送请求
      const result = await this._sendSSE(data);
      
      
      return result;
    } else {
      console.error('不支持的传输方式:', this.transport);
      throw new Error('不支持的传输方式');
    }
  }

  // SSE重连
  async reconnect(messageId) {
    console.log('开始SSE重连，messageId:', messageId);
    
    // 使用重连端点
    const originalEndpoint = this.endpoint;
    this.endpoint = `${API_BASE_URL2}${'/chat/sse/reconnect'}`;
    
    try {
      // 发送重连请求
      const result = await this._sendSSE({
        reconnect: true,
        messageId: messageId
      });
      
      return result;
    } catch (error) {
      console.error('SSE重连失败:', error);
      throw error;
    } finally {
      // 恢复原始端点
      this.endpoint = originalEndpoint;
    }
  }

  // 使用SSE发送数据
  async _sendSSE(data) {
    // 关闭之前的连接
    this.stop();

    try {
      // 准备请求参数
      const { chatlistId, content, file = [], modelId, use_search = false, use_deep_research = false,use_deep_research_step=0, reconnect = false, messageId } = data;
      
      // 构建请求体 - 统一使用JSON格式
      const requestBody = JSON.stringify({
        chatlistId: chatlistId || '',
        query: content || '',
        file: file, // 文件数据已经是格式化的 {id, name} 结构
        modelId: modelId || '',
        use_search: use_search,
        use_deep_research: use_deep_research,
        use_deep_research_step: use_deep_research_step,
        // 重连参数
        reconnect: reconnect || false,
        messageId: messageId || ''
      });
      
      const headers = {
        ...this.headers,
        'Content-Type': 'application/json'
      };

      // 创建AbortController用于取消请求
      this.controller = new AbortController();
      const { signal } = this.controller;

      // 发送POST请求获取SSE流
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: headers,
        body: requestBody,
        signal: signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // 检查响应是否是SSE流
      if (!response.headers.get('content-type')?.includes('text/event-stream')) {
        const result = await response.json();
        throw new Error(`Expected SSE stream but got: ${JSON.stringify(result)}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let lastActivityTime = Date.now(); // 记录最后活动时间
      let heartbeatTimer = null; // 心跳定时器

      // 处理SSE流
      const processStream = async () => {
        console.log('开始处理SSE流');
        
        // 设置心跳监控，每30秒检查一次连接状态
        const startHeartbeat = () => {
          if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
          }
          heartbeatTimer = setInterval(() => {
            const now = Date.now();
            const timeSinceLastActivity = now - lastActivityTime;
            
            // 使用可配置的超时时间
            if (timeSinceLastActivity > this.connectionTimeout) {
              console.log(`SSE连接心跳超时: ${timeSinceLastActivity}ms，尝试重连`);
              clearInterval(heartbeatTimer);
              this._triggerEvent('connectionTimeout', { 
                reason: 'heartbeat_timeout',
                lastActivity: lastActivityTime,
                timeSinceLastActivity
              });
              return;
            }
            
            // 输出连接状态信息
            console.log(`SSE连接正常，距离上次活动: ${timeSinceLastActivity}ms`);
          }, 30000); // 每30秒检查一次
        };
        
        // 更新活动时间
        const updateActivity = () => {
          lastActivityTime = Date.now();
        };
        
        // 启动心跳监控
        startHeartbeat();
        updateActivity(); // 初始化活动时间
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) {
              console.log('SSE流已结束');
              
              // 清理心跳定时器
              if (heartbeatTimer) {
                clearInterval(heartbeatTimer);
                heartbeatTimer = null;
              }
              
              // 在流结束前，将存储的参考资料追加到最后
              if (this.pendingSearchResults) {
                console.log('发送存储的参考资料:', this.pendingSearchResults);
                this._triggerEvent('messageChunk', {
                  messageId: this.currentMessageId,
                  content: this.pendingSearchResults
                });
                // 清除临时存储的参考资料
                this.pendingSearchResults = null;
              }
              
              // 流结束
              this._triggerEvent('messageDone', {});
              break;
            }
            
            // 更新活动时间（收到数据）
            updateActivity();
            
            // 解码并添加到缓冲区
            const chunk = decoder.decode(value, { stream: true });
            console.log('接收到SSE数据块:', chunk.length, '字节');
            buffer += chunk;
            
            // 按行处理SSE消息
            const lines = buffer.split('\n');
            buffer = lines.pop() || '';  // 最后一行可能不完整，保留到下次处理
            
            console.log(`收到${lines.length}行SSE数据`);
            
            let data = '';
            for (const line of lines) {
              if (line.startsWith('data:')) {
                data = line.slice(5).trim();
                // console.log('收到SSE数据:', data);
                
                try {
                  if (data) {
                    // 更新活动时间（收到有效数据）
                    updateActivity();
                    
                    
                    // 处理心跳消息（保持连接活动但不显示给用户）
                    if (data === '[HEARTBEAT]' || data === '[KEEPALIVE]') {
                      console.log('收到心跳信号，更新连接活动时间');
                      // 只更新活动时间，不触发任何UI事件
                      updateActivity();
                      continue;
                    }

                    
                    const parsedData = JSON.parse(data);
                    // console.log('解析后的数据:', parsedData);
                    if (parsedData.type === 'login_required') {
                      this._triggerEvent('loginRequired', {
                        message: parsedData.message
                      });
                    }

                    // 根据事件类型触发相应的回调
                    switch (parsedData.event) {
                      case 'connection':
                        console.log('处理start事件');
                        if (!this.currentMessageId) {
                          this._triggerEvent('messageStarted', {
                            messageId: parsedData.messageId,
                            chatlistId: parsedData.chatlistId
                          });
                          this.currentMessageId = parsedData.messageId;
                        }
                        break; // 添加break防止case穿透
                      case 'answer_thinking_time':
                            // 处理思考时间类型
                          console.log('处理思考时间:', parsedData.answer);
                          if (parsedData.answer !== null && parsedData.answer !== undefined && parsedData.answer !== '') {
                            this._triggerEvent('answerThinkingTime', {
                              messageId: parsedData.messageId,
                              time: parsedData.answer
                            });
                          }         
                          break;                 
                      case 'message':  // 添加对'message'事件的处理
                        if (!this.currentMessageId) {
                          this._triggerEvent('messageStarted', {
                            messageId: parsedData.messageId,
                            chatlistId: parsedData.chatlistId
                          });
                          this.currentMessageId = parsedData.messageId;
                        }
                        // 处理answer内容
                        if (parsedData.answer) {
                          // 如果type为search_result，将answer存储到临时变量中
                          if (parsedData.type === 'search_result') {
                            const referenceContent = formatReferencesToMarkdown(parsedData.answer);
                            if (referenceContent) {
                              // 存储到临时变量中，不立即发送
                              this.pendingSearchResults = referenceContent;
                              console.log('存储参考资料到临时变量:', referenceContent);
                            }
                            // search_result类型的消息不立即发送，跳过发送步骤
                          } else if (parsedData.type === 'kb_source') {
                            // 处理知识库引用类型
                            try {
                              // 解析知识库引用数据
                              const kbSourceData = JSON.parse(parsedData.answer);
                              if (Array.isArray(kbSourceData)) {
                                // 存储到临时变量中，不立即发送
                                this.pendingKbSource = kbSourceData;
                                console.log('存储知识库引用到临时变量:', kbSourceData);
                              }
                            } catch (error) {
                              console.error('解析知识库引用数据失败:', error);
                            }
                            // kb_source类型的消息不立即发送，跳过发送步骤
                          } else if (parsedData.type === 'answer_thinking') {
                            // 处理思考内容类型
                            console.log('处理思考内容:', parsedData.answer);
                            this._triggerEvent('answerThinking', {
                              messageId: parsedData.messageId,
                              content: parsedData.answer
                            });
                            // answer_thinking类型的消息不作为普通消息内容发送
                          }  else if (parsedData.type === 'researching_chunk') {
                            // 深度研究过程消息
                            this._triggerEvent('researchingChunk', {
                              messageId: parsedData.messageId,
                              content: parsedData.answer,
                              type: 'researching_chunk'
                            });
                          } else if (parsedData.type === 'research_result') {
                            // 同时作为普通消息内容发送
                            this._triggerEvent('messageChunk', {
                              messageId: parsedData.messageId,
                              content: parsedData.answer
                            });
                            // 深度研究结果消息，触发研究完成事件
                            this._triggerEvent('researchResult', {
                              messageId: parsedData.messageId,
                              content: parsedData.answer,
                              type: 'research_result'
                            });
                          } else {
                            // 非search_result类型的正常消息内容
                            this._triggerEvent('messageChunk', {
                              messageId: parsedData.messageId,
                              content: parsedData.answer,
                              chatlistId: parsedData.chatlistId
                            });
                          }
                        }
                        break;
                        
                      case 'chunk':
                        this._triggerEvent('messageChunk', {
                          messageId: parsedData.messageId,
                          content: parsedData.answer || ''
                        });
                        break;
                        
                      case 'completion':
                        // 清理心跳定时器
                        if (heartbeatTimer) {
                          clearInterval(heartbeatTimer);
                          heartbeatTimer = null;
                        }

                        // 在流结束前，将存储的参考资料追加到最后
                        if (this.pendingSearchResults) {
                          console.log('发送存储的参考资料:', this.pendingSearchResults);
                          console.log('附带参考资料的消息ID:', this.currentMessageId );
                          this._triggerEvent('messageChunk', {
                            messageId: this.currentMessageId ,
                            content: this.pendingSearchResults
                          });
                          // 清除临时存储的参考资料
                          this.pendingSearchResults = null;
                        }

                        // 在参考资料之后，发送知识库引用
                        if (this.pendingKbSource) {
                          console.log('发送存储的知识库引用:', this.pendingKbSource);
                          this._triggerEvent('kbSource', {
                            messageId: this.currentMessageId,
                            kbSource: this.pendingKbSource
                          });
                          // 清除临时存储的知识库引用
                          this.pendingKbSource = null;
                        }

                        this._triggerEvent('messageDone', {
                          chatlistId: parsedData.chatlistId,
                          messageId: parsedData.messageId
                        });
                        this.currentMessageId = null;
                        break;
                        
                      default:
                        // 未知事件类型
                        console.log('未知SSE事件类型:', parsedData.event);
                        break; // 添加默认情况的break
                    }
                  }
                } catch (error) {
                  // 直接忽略解析错误，但打印出来便于调试
                  console.log('解析数据:', data);
                  console.log('解析错误:', error);
                }
              }
            }
          }
        } catch (error) {
          // 清理心跳定时器
          if (heartbeatTimer) {
            clearInterval(heartbeatTimer);
            heartbeatTimer = null;
          }
          
          if (error.name === 'AbortError') {
            console.log('Stream reading was aborted');
          } else {
            console.error('Error reading stream:', error);
            this._triggerEvent('error', error);
          }
        }
      };

      // 开始处理流并返回
      processStream().catch(error => {
        console.error('处理SSE流时发生错误:', error);
        
        // 清理心跳定时器
        if (heartbeatTimer) {
          clearInterval(heartbeatTimer);
          heartbeatTimer = null;
        }
        
        // 在错误时也要发送存储的参考资料
        if (this.pendingSearchResults) {
          console.log('错误时发送存储的参考资料:', this.pendingSearchResults);
          this._triggerEvent('messageChunk', {
            messageId: this.currentMessageId,
            content: this.pendingSearchResults
          });
          this.pendingSearchResults = null;
        }

        // 在错误时也要发送知识库引用
        if (this.pendingKbSource) {
          console.log('错误时发送知识库引用:', this.pendingKbSource);
          this._triggerEvent('kbSource', {
            messageId: this.currentMessageId,
            kbSource: this.pendingKbSource
          });
          this.pendingKbSource = null;
        }

        // 确保在错误时也触发messageDone事件
        this._triggerEvent('messageDone', {
          messageId: this.currentMessageId,
          error: true
        });
        this.currentMessageId = null;
      });
      
      // 设置终极兜底超时，确保在10秒后都会触发messageDone
      // setTimeout(() => {
      //   if (this.currentMessageId) {
      //     console.log('兜底超时触发，强制结束SSE响应');
          
      //     // 超时时也要发送存储的参考资料
      //     if (this.pendingSearchResults) {
      //       console.log('超时时发送存储的参考资料:', this.pendingSearchResults);
      //       this._triggerEvent('messageChunk', {
      //         messageId: this.currentMessageId,
      //         content: this.pendingSearchResults
      //       });
      //       this.pendingSearchResults = null;
      //     }
          
      //     this._triggerEvent('messageDone', {
      //       messageId: this.currentMessageId,
      //       timeout: true
      //     });
      //     this.currentMessageId = null;
      //   }
      // }, 10000);
      
      return true;
    } catch (error) {
      console.error('发送SSE请求失败:', error);
      
      // 确保在错误时也触发messageDone事件
      this._triggerEvent('messageDone', {
        error: true
      });
      
      throw error;
    }
  }

  // 停止流式请求
  stop() {
    // 清理心跳定时器
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }

    if (this.controller) {
      this.controller.abort();
      this.controller = null;
    }
    this.currentMessageId = null;
    this.pendingSearchResults = null; // 清除临时存储的参考资料
    this.pendingKbSource = null; // 清除临时存储的知识库引用
    this.pendingResearchContent = null; // 清除深度研究临时数据
  }
}

// 创建AG-UI客户端实例
 const agui = new AGUIClientMock({
  transport: 'sse',
  endpoint: `${API_BASE_URL2}${'/chat/sse'}`,  // 移除多余的/api前缀，因为API_BASE_URL已经包含了
  headers: {
    'Content-Type': 'application/json',
  },
  // 针对1小时长任务：设置60分钟超时
  connectionTimeout: 60 * 60 * 1000  // 60分钟超时
});

// 设置全局headers
export const setAGUIHeaders = (token) => {
  if (token) {
    agui.setHeaders({
      'Authorization': `Bearer ${token}`,
      'token': token  // 添加token字段，某些API可能需要
    });
  }
};

// 设置连接超时时间
export const setConnectionTimeout = (timeout) => {
  agui.setConnectionTimeout(timeout);
};

// 获取连接超时时间
export const getConnectionTimeout = () => {
  return agui.getConnectionTimeout();
};

export default agui; 